/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';

import Modal from 'react-bootstrap/Modal';
import { useQuery, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import closeIcon from '../../assets/images/closeIcon.svg';
import userIcon from '../../assets/images/usericon.webp';
import lockIcon from '../../assets/images/lockSmall.svg';
import RoundedInput from '../Inputs/RoundedInput';
import FilledButton from '../Buttons/FilledButton';
import Textarea from '../Textarea';
import EmojiPickerWithButton from '../EmojiPickerWithButton';
import InfoIcon from '../InfoIcon';
import Option from '../Option';
import Checkbox from '../Checkbox';
import PermissionService from '../../services/PermissionService';
import OrganizationService from '../../services/OrganizationService';
import {
  RESOURCE_TYPES,
  MEETING_ROLES,
  getAvailableRoles,
  getRoleDisplayName,
  getRoleHint,
  getResourceDisplayName,
} from '../../services/permissions/constants';
import getUserPhotoUrl from '../../helpers/getUserPhotoUrl';
import validateEmail from '../../helpers/validateEmail';
import { UiContext } from '../../context/UiContext';
// eslint-disable-next-line import/no-named-as-default
import useAuthorization from '../../hooks/useAuthorization';
// import ShareableLinksSection from './ShareableLinksSection';
import classes from './styles.module.scss';

export default function PermissionManager({
  show,
  onClose,
  resourceId,
  resourceType,
  resourceName,
  user, // Current user for permission checks
  onResourceUpdate, // Callback to refetch resource data after updates (e.g., users/roles)
  initialShowAddForm = false, // If true, show add form immediately when modal opens
  folderId, // Optional: Folder ID (for meeting resources, to check folder-level permissions)
  folderUserRole, // Optional: User's role in the folder (for meeting resources)
}) {
  const navigate = useNavigate();

  // For organization resources, show create user form by default when opened from User Management
  const shouldShowCreateUserByDefault = 
    resourceType === RESOURCE_TYPES.ORGANIZATION && initialShowAddForm;
  
  const [isAddingUsers, setIsAddingUsers] = useState(
    initialShowAddForm && !shouldShowCreateUserByDefault
  );
  const [emails, setEmails] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [message, setMessage] = useState('');
  const [emailsValidationError, setEmailsValidationError] = useState('');
  const [areEmailsValidated, setAreEmailsValidated] = useState(false);
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(shouldShowCreateUserByDefault);
  const [newUserData, setNewUserData] = useState({
    name: '',
    title: '',
    email: '',
  });
  const [newUserErrors, setNewUserErrors] = useState({});
  const [isStatusExpanded, setIsStatusExpanded] = useState(false);
  const [selectedFolderIds, setSelectedFolderIds] = useState([]);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);

  const { notifyError, notifySuccess, showConfirmActionModal } = useContext(UiContext);

  const isFolderResource = resourceType === RESOURCE_TYPES.FOLDER;
  const resourceDisplayName = useMemo(
    () => getResourceDisplayName(resourceType),
    [resourceType]
  );

  // Get available roles for this resource type
  const availableRoles = useMemo(() => {
    const baseRoles = getAvailableRoles(resourceType);
    if (resourceType === RESOURCE_TYPES.MEETING) {
      return baseRoles.filter(
        (role) =>
          role !== MEETING_ROLES.CREATOR &&
          role !== MEETING_ROLES.CONTRIBUTOR_CLOSED
      );
    }
    return baseRoles;
  }, [resourceType]);
  const defaultRole = availableRoles.includes('viewer')
    ? 'viewer'
    : availableRoles[0] || '';

  // Set default role when resource type changes
  useEffect(() => {
    if (availableRoles.length > 0 && !selectedRole) {
      setSelectedRole(defaultRole);
    }
  }, [resourceType, availableRoles, defaultRole, selectedRole]);

  // Fetch folders when viewer role is selected for organization
  useEffect(() => {
    const fetchFolders = async () => {
      if (
        resourceType === RESOURCE_TYPES.ORGANIZATION &&
        selectedRole === 'viewer' &&
        resourceId &&
        isCreatingNewUser
      ) {
        setIsLoadingFolders(true);
        try {
          const folders = await OrganizationService.getOrganizationFolders(resourceId);
          setAvailableFolders(folders || []);
        } catch (error) {
          console.error('Failed to fetch folders:', error);
          notifyError('Failed to load folders');
          setAvailableFolders([]);
        } finally {
          setIsLoadingFolders(false);
        }
      } else {
        setAvailableFolders([]);
        setSelectedFolderIds([]);
      }
    };

    fetchFolders();
  }, [resourceType, selectedRole, resourceId, isCreatingNewUser, notifyError]);

  // Fetch users with access
  const {
    data: users = [],
    isLoading,
    refetch,
  } = useQuery(
    ['permissionUsers', resourceId, resourceType, show],
    () => PermissionService.getUsers(resourceId, resourceType),
    {
      enabled: !!resourceId && !!resourceType && show,
      keepPreviousData: true,
    }
  );

  // Hide inactive org users from the visible list to avoid confusion
  const visibleUsers = useMemo(() => {
    if (!users) return [];
    if (resourceType === RESOURCE_TYPES.ORGANIZATION) {
      return users.filter((permission) => permission.user?.isActive !== false);
    }
    return users;
  }, [users, resourceType]);

  // Get display role label helper function (defined early for use in mutations)
  const getDisplayRoleLabel = useCallback(
    (role, options = {}) => {
      if (!role) return '';
      const { preserveCreatorLabel = false } = options;
      if (resourceType === RESOURCE_TYPES.MEETING) {
        if (role === MEETING_ROLES.CREATOR) {
          return preserveCreatorLabel
            ? getRoleDisplayName(role, resourceType)
            : 'Admin';
        }
        if (role === MEETING_ROLES.CONTRIBUTOR_CLOSED) {
          return getRoleDisplayName(MEETING_ROLES.CONTRIBUTOR, resourceType);
        }
      }
      return getRoleDisplayName(role, resourceType);
    },
    [resourceType]
  );

  // Add users mutation
  const { mutate: addUsers, isLoading: isAdding } = useMutation(
    async ({ emailsList, role, messageText, newUsersPayload, folderIds }) => {
      return PermissionService.assignRole(
        resourceId,
        resourceType,
        emailsList,
        role,
        { message: messageText, newUsers: newUsersPayload, folderIds }
      );
    },
    {
      onSuccess: (data, variables) => {
        const { emailsList, role: userRole, newUsersPayload: newUsers } = variables;
        const userCount = Array.isArray(emailsList) ? emailsList.length : 1;
        const roleLabel = getDisplayRoleLabel(userRole);
        
        if (resourceType === RESOURCE_TYPES.ORGANIZATION && newUsers) {
          // New user account created
          notifySuccess(
            userCount === 1
              ? `Invitation sent to ${emailsList[0]}`
              : `Invitations sent to ${userCount} users`
          );
        } else {
          // Existing users added
          notifySuccess(
            userCount === 1
              ? `User added as ${roleLabel}`
              : `${userCount} users added as ${roleLabel}`
          );
        }
        
        refetch();
        if (onResourceUpdate) {
          onResourceUpdate();
        }
        setIsAddingUsers(false);
        setEmails('');
        setMessage('');
        setSelectedRole(defaultRole);
        setEmailsValidationError('');
        setIsCreatingNewUser(false);
        setNewUserData({ name: '', title: '', email: '' });
        setNewUserErrors({});
        setSelectedFolderIds([]);
      },
      onError: (error) => {
        const data = error?.response?.data;
        const firstError =
          Array.isArray(data?.errors) && data.errors[0]?.error
            ? data.errors[0].error
            : null;
        notifyError(firstError || data?.message || error?.message || 'Failed to add users');
      },
    }
  );

  // Update role mutation
  const { mutate: updateRole, mutateAsync: updateRoleAsync } = useMutation(
    async ({ userId, role }) => {
      return PermissionService.updateRole(
        resourceId,
        resourceType,
        userId,
        role
      );
    },
    {
      onSuccess: (data, variables) => {
        const { role: newRole } = variables;
        const roleLabel = getDisplayRoleLabel(newRole);
        const targetUser = users.find((u) => u.userId === variables.userId);
        const userName = targetUser?.user?.name || targetUser?.user?.email || 'User';
        
        if (newRole === 'owner') {
          notifySuccess(`Ownership transferred to ${userName}`);
        } else {
          notifySuccess(`${userName}'s role updated to ${roleLabel}`);
        }
        
        refetch();
        // If ownership was transferred, refetch the resource data and close modal
        if (newRole === 'owner') {
          if (onResourceUpdate) {
            onResourceUpdate();
          }
          // Close modal since user no longer has management access
          setTimeout(() => {
            onClose();
          }, 500); // Small delay to allow refetch to complete
        }
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to update role'
        );
      },
    }
  );

  // Remove user mutation
  const { mutate: removeUser } = useMutation(
    async ({ userId }) => {
      return PermissionService.removeUser(
        resourceId,
        resourceType,
        userId
      );
    },
    {
      onSuccess: (data, variables) => {
        const targetUser = users.find((u) => u.userId === variables.userId);
        const userName = targetUser?.user?.name || targetUser?.user?.email || 'User';
        notifySuccess(`${userName} removed successfully`);
        refetch();
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to remove user'
        );
      },
    }
  );

  // Get current user's resource-level permission
  const currentUserPermission = useMemo(() => {
    if (!user || !users?.length) {
      return null;
    }
    return users.find((permission) => permission.userId === user.id) || null;
  }, [users, user]);

  // Get resource object for authorization hook
  // We need the resource owner's userId to check ownership
  const resource = useMemo(() => {
    if (!users.length) return null;
    // Find the owner/creator to get the resource owner's userId
    const owner = users.find((u) => 
      u.role === 'owner' || u.role === 'creator'
    );
    // If no owner found, check if current user is the owner (for new resources)
    if (!owner && currentUserPermission?.role === 'owner') {
      return { userId: user.id };
    }
    return owner ? { userId: owner.userId } : null;
  }, [users, currentUserPermission, user]);

  // Use centralized authorization hook with specific permissions
  const {
    canManageMeetingPermissions,
    canManageFolderPermissions,
    isOrgOwner,
    isFolderOwner,
    canViewAccessList: canViewAccessListFromHook,
  } = useAuthorization(
    user,
    resource,
    currentUserPermission,
    resourceType,
    {
      folderId,
      folderUserRole,
    }
  );


  // Determine which permission to use based on resource type
  const canManage = useMemo(() => {
    if (resourceType === RESOURCE_TYPES.MEETING) return canManageMeetingPermissions;
    if (resourceType === RESOURCE_TYPES.FOLDER) return canManageFolderPermissions;
    if (resourceType === RESOURCE_TYPES.ORGANIZATION) return isOrgOwner;
    return false;
  }, [resourceType, canManageMeetingPermissions, canManageFolderPermissions, isOrgOwner]);

  const canViewAccessList = canViewAccessListFromHook;
  const canCreateOrgUsers =
    canManage && resourceType === RESOURCE_TYPES.ORGANIZATION;

  const handleAddUsers = async () => {
    let isValid = true;
    setEmailsValidationError('');

    let emailsList = [];

    if (!emails) {
      setEmailsValidationError('This field is required');
      isValid = false;
    } else {
      emailsList = emails.split(';').join(',').split(',');
      emailsList = emailsList.map((mail) => mail.trim()).filter(Boolean);

      const isEveryEmailValid = emailsList.every((mail) => validateEmail(mail));

      if (!isEveryEmailValid) {
        setEmailsValidationError('Emails must be valid');
        isValid = false;
      }
    }

    if (!selectedRole) {
      setEmailsValidationError('Please select a role');
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    if (isFolderResource && selectedRole === 'owner') {
    const emailPermissionMap = users.reduce((acc, permission) => {
      if (permission.user?.email) {
        acc[permission.user.email.trim().toLowerCase()] = permission;
      }
      return acc;
    }, {});

    const emailIsExisting = (mail) =>
      !!emailPermissionMap[mail.trim().toLowerCase()];
    const missingEmails = emailsList.filter((mail) => !emailIsExisting(mail));

      if (missingEmails.length > 0) {
        setEmailsValidationError(
          'Ownership can only be transferred to someone who already has access. Invite them first, then transfer ownership.'
        );
        return;
      }

      try {
        await Promise.all(
          emailsList.map((mail) => {
            const permission = emailPermissionMap[mail.trim().toLowerCase()];
            return updateRoleAsync({ userId: permission.userId, role: 'owner' });
          })
        );
        setIsAddingUsers(false);
        setEmails('');
        setMessage('');
        setSelectedRole(defaultRole);
        setEmailsValidationError('');
      } catch (error) {
        notifyError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to transfer ownership'
        );
      }
      return;
    }

    addUsers({
      emailsList,
      role: selectedRole,
      messageText: message,
      newUsersPayload: null,
    });
  };

  const handleOpenCreateUserForm = (prefillEmail = '') => {
    if (!canCreateOrgUsers) {
      return;
    }
    setIsAddingUsers(false);
    setIsCreatingNewUser(true);
    setNewUserErrors({});
    setNewUserData({
      name: '',
      title: '',
      email: prefillEmail || '',
    });
  };

  const handleCancelCreateUser = () => {
    setIsCreatingNewUser(false);
    setNewUserData({ name: '', title: '', email: '' });
    setNewUserErrors({});
    // If opened from User Management, go back to user list view instead of add form
    if (resourceType === RESOURCE_TYPES.ORGANIZATION && initialShowAddForm) {
      setIsAddingUsers(false);
    }
  };

  const handleGoToMyLibrary = () => {
    onClose();
    navigate('/my-library');
  };

  const handleCreateUserSubmit = () => {
    if (resourceType !== RESOURCE_TYPES.ORGANIZATION) {
      return;
    }

    const trimmedEmail = newUserData.email.trim();
    const trimmedName = newUserData.name.trim();
    const trimmedTitle = newUserData.title.trim();
    const errorsMap = {};

    if (!trimmedName) {
      errorsMap.name = 'Name is required';
    }

    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
      errorsMap.email = 'Valid email is required';
    }

    if (!selectedRole) {
      errorsMap.role = 'Please select a role';
    }

    // Require at least one folder for viewer role
    if (selectedRole === 'viewer' && selectedFolderIds.length === 0) {
      errorsMap.folders =
        !isLoadingFolders && availableFolders.length === 0
          ? 'Library folders must be created before adding viewers'
          : 'Please select at least one folder';
    }

    setNewUserErrors(errorsMap);

    if (Object.keys(errorsMap).length > 0) {
      return;
    }

    addUsers({
      emailsList: [trimmedEmail],
      role: selectedRole,
      messageText: '',
      newUsersPayload: [
        {
          email: trimmedEmail,
          name: trimmedName,
          title: trimmedTitle,
        },
      ],
      folderIds: selectedRole === 'viewer' ? selectedFolderIds : undefined,
    });
  };

  // Handle role change
  const handleRoleChange = (userId, newRole) => {
    updateRole({ userId, role: newRole });
  };

  // Handle remove user
  const handleRemoveUser = (userId) => {
    showConfirmActionModal({
      title: 'Remove access?',
      message: `Are you sure you want to remove this user? They will no longer have access to this ${resourceDisplayName}.`,
      onConfirm: () => {
        removeUser({ userId });
      },
    });
  };

  // Handle transfer ownership (folders only)
  const handleTransferOwnership = (userId) => {
    showConfirmActionModal({
      title: 'Make this person the owner?',
      message: 'The new owner will be notified, and you will no longer have owner rights to this folder.',
      onConfirm: () => {
        updateRole({ userId, role: 'owner' });
      },
    });
  };

  // Reset form when modal closes or opens
  useEffect(() => {
    if (!show) {
      setIsAddingUsers(false);
      setEmails('');
      setMessage('');
      setSelectedRole(defaultRole);
      setEmailsValidationError('');
      setIsCreatingNewUser(false);
      setNewUserData({ name: '', title: '', email: '' });
      setNewUserErrors({});
      setIsStatusExpanded(false);
    } else {
      // When modal opens, set initial state
      // For organization resources opened from User Management, show create user form
      const shouldShowCreate = 
        resourceType === RESOURCE_TYPES.ORGANIZATION && initialShowAddForm;
      setIsAddingUsers(initialShowAddForm && !shouldShowCreate);
      setIsCreatingNewUser(shouldShowCreate);
    }
  }, [show, defaultRole, initialShowAddForm, resourceType]);

  const showCreateUserView = canCreateOrgUsers && isCreatingNewUser;
  const availableRolesForExistingUsers =
    isFolderResource && !isAddingUsers ? availableRoles.filter((role) => role !== 'owner') : availableRoles;

  // Contributors removed from permission management, so no need to filter roles
  const availableRolesForAddingUsers = availableRoles;

  const currentUserRole = currentUserPermission?.role;
  const currentUserRoleDisplay = currentUserRole
    ? getDisplayRoleLabel(currentUserRole, { preserveCreatorLabel: true })
    : '';
  const currentUserRoleHint =
    (currentUserRole && getRoleHint(resourceType, currentUserRole)) || '';

  const organizationOwnerDescription =
    'As an organization admin, you can manage all folders, videos, and users in your organization, even if local permissions are limited.';
  const folderOwnerDescription =
    'As a folder owner, you can manage all videos and users in your folder, even if individual permissions are limited.';
  const shouldShowResourceRoleAlongsideOrgOwner =
    isOrgOwner &&
    currentUserRoleDisplay &&
    resourceType !== RESOURCE_TYPES.ORGANIZATION;
  const shouldShowResourceRoleAlongsideFolderOwner =
    isFolderOwner &&
    !isOrgOwner &&
    currentUserRoleDisplay &&
    resourceType !== RESOURCE_TYPES.FOLDER;
  let capabilityDescription;
  if (isOrgOwner) {
    const additionalText = shouldShowResourceRoleAlongsideOrgOwner && currentUserRoleHint
      ? ` You currently have ${currentUserRoleDisplay.toLowerCase()} access for this ${resourceDisplayName}, but organization ownership takes precedence.`
      : '';
    capabilityDescription = `${organizationOwnerDescription}${additionalText}`;
  } else if (isFolderOwner) {
    const additionalText = shouldShowResourceRoleAlongsideFolderOwner && currentUserRoleHint
      ? ` You currently have ${currentUserRoleDisplay.toLowerCase()} access for this ${resourceDisplayName}, but folder ownership takes precedence.`
      : '';
    capabilityDescription = `${folderOwnerDescription}${additionalText}`;
  } else {
    capabilityDescription = currentUserRoleHint;
  }
  const fallbackRoleDescription =
    currentUserRoleDisplay && resourceType
      ? `You currently have ${currentUserRoleDisplay.toLowerCase()} access to this ${resourceDisplayName}.`
      : '';
  const showCurrentUserStatus =
    !!currentUserPermission &&
    !isLoading &&
    !isAddingUsers &&
    !showCreateUserView;
  const toggleStatusExpanded = () => {
    setIsStatusExpanded((prev) => !prev);
  };

  return (
    <Modal
      show={show}
      centered
      contentClassName={classes.PermissionManager}
      dialogClassName={classes.dialog}
      backdropClassName={classes.backdrop}
      onHide={onClose}
      data-testid={`permission-manager-modal-${resourceType}-${resourceId}`}
    >
      <i className={classes.closeIcon} onClick={onClose}>
        <img src={closeIcon} alt="Close" />
      </i>
      <header data-testid="permission-manager-header">
        <h1>Manage Access</h1>
        <p className={classes.subtitle}>{resourceName}</p>
      </header>

      <div className={classes.content}>
        {showCurrentUserStatus && (
          <div
            className={`${classes.currentUserStatus} ${
              isStatusExpanded ? classes.currentUserStatusExpanded : ''
            }`}
            onClick={toggleStatusExpanded}
          >
            <div className={classes.statusHeader}>
              <div className={classes.statusIdentityRow}>
                <img
                  src={lockIcon}
                  alt=""
                  aria-hidden="true"
                  className={classes.statusLockIcon}
                />
                <div className={classes.statusIdentity}>
                  <span className={classes.statusLabel}>You are signed in as</span>
                  <span className={classes.statusName}>
                    {user?.name || user?.email || 'Current user'}
                  </span>
                  {isStatusExpanded && user?.email && (
                    <span className={classes.statusEmail}>{user.email}</span>
                  )}
                </div>
              </div>
              <div className={classes.statusBadges}>
                {isOrgOwner && (
                  <span
                    className={`${classes.statusRoleBadge} ${classes.primaryRoleBadge}`}
                  >
                    Organization Admin
                  </span>
                )}
                {isFolderOwner && !isOrgOwner && (
                  <span
                    className={`${classes.statusRoleBadge} ${classes.primaryRoleBadge}`}
                  >
                    Folder Owner
                  </span>
                )}
                {currentUserRoleDisplay &&
                  (!isOrgOwner || shouldShowResourceRoleAlongsideOrgOwner) &&
                  (!isFolderOwner || isOrgOwner || shouldShowResourceRoleAlongsideFolderOwner) && (
                    <span className={classes.statusRoleBadge}>
                      {currentUserRoleDisplay}
                    </span>
                  )}
              </div>
            </div>
            {isStatusExpanded && (
              <p className={`${classes.statusDescription} ${classes.statusDescriptionExpanded}`}>
                {capabilityDescription || fallbackRoleDescription}
              </p>
            )}
          </div>
        )}
        {/* Users List - Hide when adding users */}
        {canViewAccessList && !isAddingUsers && !showCreateUserView && (
          <div className={classes.usersSection}>
            <div className={classes.sectionHeader}>
              <h2>People with access</h2>
              {canManage && (
                <FilledButton
                  onClick={() => setIsAddingUsers(true)}
                  width={196}
                  style={{ fontSize: '18px', padding: '12px 20px' }}
                  data-testid="permission-manager-add-people-button"
                >
                  + Add People
                </FilledButton>
              )}
            </div>

            {isLoading && <div className={classes.loading}>Loading...</div>}
            {!isLoading && visibleUsers.length === 0 && (
              <div className={classes.emptyState}>
                No users have access yet. Click &quot;Add People&quot; to share.
              </div>
            )}
            {!isLoading && visibleUsers.length > 0 && (
            <ul className={classes.usersList}>
              {visibleUsers.map((permission) => (
                <li key={permission.id} className={classes.userItem}>
                  <div className={classes.userInfo}>
                    <img
                      src={
                        getUserPhotoUrl(permission.user?.photoPath) || userIcon
                      }
                      alt={permission.user?.name || 'User'}
                      className={classes.userAvatar}
                    />
                    <div className={classes.userDetails}>
                      <span className={classes.userName}>
                        {permission.user?.name || 'Unknown User'}
                      </span>
                      {permission.user?.title && (
                        <span className={classes.userTitle}>
                          {permission.user.title}
                        </span>
                      )}
                      {permission.user?.email && (
                        <a
                          className={classes.userEmail}
                          href={`mailto:${permission.user.email}`}
                        >
                          {permission.user.email}
                        </a>
                      )}
                    </div>
                  </div>
                      {canManage && permission.userId !== user?.id ? (
                        <div className={classes.userActions}>
                          {(resourceType === RESOURCE_TYPES.FOLDER ||
                            resourceType === RESOURCE_TYPES.ORGANIZATION) &&
                            (permission.role === 'owner' ? (
                              <span className={classes.ownerBadge}>Admin</span>
                            ) : (
                              <select
                                className={classes.roleSelect}
                                value={permission.role}
                                onChange={(e) =>
                                  handleRoleChange(
                                    permission.userId,
                                    e.target.value
                                  )
                                }
                              >
                                {availableRolesForExistingUsers.map((role) => (
                                  <option key={role} value={role}>
                                    {getDisplayRoleLabel(role)}
                                  </option>
                                ))}
                              </select>
                            ))}
                          {(resourceType === RESOURCE_TYPES.FOLDER ||
                            resourceType === RESOURCE_TYPES.ORGANIZATION) &&
                            permission.role !== 'owner' && (
                              <button
                                className={classes.removeButton}
                                onClick={() =>
                                  handleRemoveUser(permission.userId)
                                }
                                type="button"
                              >
                                Remove
                              </button>
                          )}
                          {resourceType === RESOURCE_TYPES.FOLDER &&
                            permission.role !== 'owner' && (
                              <button
                                className={classes.transferButton}
                                onClick={() =>
                                  handleTransferOwnership(permission.userId)
                                }
                                type="button"
                              >
                                Transfer
                              </button>
                            )}
                          {resourceType === RESOURCE_TYPES.MEETING && (
                            <>
                              {permission.role === MEETING_ROLES.CREATOR ? (
                                <span className={classes.ownerBadge}>
                                  {getDisplayRoleLabel(permission.role, {
                                    preserveCreatorLabel: true,
                                  })}
                            </span>
                              ) : (
                                <>
                                  <select
                                    className={classes.roleSelect}
                                    value={permission.role}
                                    onChange={(e) =>
                                      handleRoleChange(
                                        permission.userId,
                                        e.target.value
                                      )
                                    }
                                  >
                                    {availableRoles.map((role) => (
                                      <option key={role} value={role}>
                                        {getDisplayRoleLabel(role)}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    className={classes.removeButton}
                                    onClick={() =>
                                      handleRemoveUser(permission.userId)
                                    }
                                    type="button"
                                  >
                                    Remove
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                    <span className={classes.roleBadge}>
                          {getDisplayRoleLabel(permission.role, {
                            preserveCreatorLabel: true,
                          })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            )}
          </div>
        )}

        {/* Shareable Links Section - Only for meetings (temporarily hidden)
        {resourceType === RESOURCE_TYPES.MEETING && canManage && !isAddingUsers && !showCreateUserView && (
          <ShareableLinksSection
            meetingId={resourceId}
            canManage={canManage}
          />
        )} */}

        {/* Add Users Form */}
        {isAddingUsers && canManage && !showCreateUserView && (
          <div className={classes.addUsersSection}>
            <div className={classes.sectionHeader}>
              <h2>Add People</h2>
            </div>

            <div className={classes.addForm}>
              <div className={classes.formRow}>
                <div className={classes.formCol}>
                  <div className={classes.formTitle}>
                    <h3>
                      Invite others
                      <div className={classes.infoIconContainer}>
                        <InfoIcon
                          text={`Use a semicolon ";" after each email address when adding multiple emails`}
                          width={285}
                        />
                      </div>
                    </h3>
                  </div>
                  <RoundedInput
                    type="text"
                    placeholder="Add email addresses"
                    value={emails}
                    onChange={(event) => setEmails(event.target.value)}
                    setValue={setEmails}
                    users
                    style={{ height: 65 }}
                    canCreateUser={canCreateOrgUsers}
                    onCreateUserRequest={(inviteEmail) =>
                      handleOpenCreateUserForm(inviteEmail)
                    }
                    requireSuggestionSelection
                    onValidationChange={setAreEmailsValidated}
                  />
                  {emailsValidationError && (
                    <div className={classes.error}>{emailsValidationError}</div>
                  )}
                </div>

                    <div className={classes.formCol}>
                      <div className={classes.formTitle}>
                        <h3>Select Role</h3>
                      </div>
                      <div className={classes.roleOptions}>
                        {availableRolesForAddingUsers.map((role) => (
                          <Option
                            key={role}
                            isChecked={selectedRole === role}
                            onClick={() => setSelectedRole(role)}
                          >
                            {getDisplayRoleLabel(role)}
                            <InfoIcon text={getRoleHint(resourceType, role)} />
                          </Option>
                        ))}
                      </div>
                      {isFolderResource && selectedRole === 'owner' && (
                        <div className={classes.roleWarning}>
                          <strong>Heads up:</strong> inviting someone as owner transfers all folder privileges to them and removes your owner access.
                        </div>
                      )}
                    </div>
              </div>

              <div className={classes.formRow}>
                <div className={classes.formCol}>
                  <div className={classes.formTitle}>
                    <h3>Message (optional)</h3>
                  </div>
                  <div className={classes.messageContainer}>
                    <Textarea
                      placeholder="Start Typing..."
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      style={{ height: 172 }}
                    />
                    <EmojiPickerWithButton
                      setCommentText={setMessage}
                      buttonBottom="24px"
                      buttonRight="24px"
                      pickerTop="52px"
                    />
                  </div>
                </div>
              </div>

              <div className={classes.formActions}>
                <div className={classes.formActionsRight}>
                  <button
                    className={classes.cancelFormButton}
                    onClick={() => {
                      setIsAddingUsers(false);
                      setEmails('');
                      setMessage('');
                      setSelectedRole(defaultRole);
                      setEmailsValidationError('');
                    }}
                    type="button"
                  >
                    Cancel
                  </button>
                  <FilledButton
                    onClick={handleAddUsers}
                    width={192}
                    isDisabled={isAdding || !areEmailsValidated || !emails.trim()}
                    data-testid="permission-manager-share-button"
                  >
                    {isAdding ? 'Adding...' : 'Add People'}
                  </FilledButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCreateUserView && (
          <div className={classes.addUsersSection}>
            <div className={classes.sectionHeader}>
                <h2>Create new user account</h2>
            </div>

            <div className={classes.addForm}>
              <div className={classes.formRow}>
                <div className={classes.formCol}>
                  <div className={classes.formTitle}>
                    <h3>Full name</h3>
                  </div>
                  <RoundedInput
                    type="text"
                    placeholder="Enter full name"
                    value={newUserData.name}
                    onChange={(event) =>
                      setNewUserData((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                    style={{ height: 65 }}
                  />
                  {newUserErrors.name && (
                    <div className={classes.error}>{newUserErrors.name}</div>
                  )}
                </div>
                <div className={classes.formCol}>
                  <div className={classes.formTitle}>
                    <h3>Title</h3>
                  </div>
                  <RoundedInput
                    type="text"
                    placeholder="Title (optional)"
                    value={newUserData.title}
                    onChange={(event) =>
                      setNewUserData((prev) => ({
                        ...prev,
                        title: event.target.value,
                      }))
                    }
                    style={{ height: 65 }}
                  />
                </div>
              </div>

              <div className={`${classes.formRow} ${classes.singleColumnRow}`}>
                <div className={classes.formCol}>
                  <div className={classes.formTitle}>
                    <h3>Email address</h3>
                  </div>
                  <RoundedInput
                    type="email"
                    placeholder="user@company.com"
                    value={newUserData.email}
                    onChange={(event) =>
                      setNewUserData((prev) => ({
                        ...prev,
                        email: event.target.value,
                      }))
                    }
                    style={{ height: 65 }}
                  />
                  {newUserErrors.email && (
                    <div className={classes.error}>{newUserErrors.email}</div>
                  )}
                </div>
              </div>

              <div className={`${classes.formRow} ${classes.singleColumnRow}`}>
                <div className={classes.formCol}>
                  <div className={classes.formTitle}>
                    <h3>Select Role</h3>
                  </div>
                  <div className={classes.roleOptionsInline}>
                    {availableRoles.map((role) => (
                      <Option
                        key={role}
                        isChecked={selectedRole === role}
                        onClick={() => setSelectedRole(role)}
                      >
                        {getDisplayRoleLabel(role)}
                        <InfoIcon text={getRoleHint(resourceType, role)} />
                      </Option>
                    ))}
                  </div>
                  {newUserErrors.role && (
                    <div className={classes.error}>{newUserErrors.role}</div>
                  )}
                </div>
              </div>

              {selectedRole === 'owner' && resourceType === RESOURCE_TYPES.ORGANIZATION && (
                <div className={`${classes.formRow} ${classes.singleColumnRow} ${classes.tightSpacing}`}>
                  <div className={classes.formCol}>
                    <div className={classes.ownerRoleBanner}>
                      <strong>Organization Admin</strong>
                      <p>
                        This role gives full access to all organization settings, user management, and all folders and videos within the organization. Admins can manage permissions, invite users, and access all content.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedRole === 'viewer' && resourceType === RESOURCE_TYPES.ORGANIZATION && (
                <div className={`${classes.formRow} ${classes.singleColumnRow} ${classes.tightSpacing}`}>
                  <div className={classes.formCol}>
                    {isLoadingFolders && (
                      <div className={classes.loading}>Loading folders...</div>
                    )}
                    {!isLoadingFolders && availableFolders.length === 0 && (
                      <div className={classes.noFoldersBanner}>
                        <strong>Oops!</strong>
                        <p>
                          Library folders must be created before adding viewers. Please click{' '}
                          <button
                            type="button"
                            className={classes.bannerLink}
                            onClick={handleGoToMyLibrary}
                          >
                            here
                          </button>{' '}
                          to go to &quot;My Library&quot;.
                        </p>
                      </div>
                    )}
                    {!isLoadingFolders && availableFolders.length > 0 && (
                      <>
                        <div className={classes.formTitle}>
                          <h3>Select Folders</h3>
                          <span className={classes.folderCount}>
                            {selectedFolderIds.length} folder{selectedFolderIds.length !== 1 ? 's' : ''} selected
                          </span>
                        </div>
                        <div className={classes.folderSelection}>
                          <div className={classes.folderSelectAll}>
                            <Checkbox
                              isChecked={
                                selectedFolderIds.length === availableFolders.length &&
                                availableFolders.length > 0
                              }
                              onClick={() => {
                                if (selectedFolderIds.length === availableFolders.length) {
                                  setSelectedFolderIds([]);
                                } else {
                                  setSelectedFolderIds(availableFolders.map((f) => f.id));
                                }
                              }}
                              label="Select All"
                            />
                          </div>
                          <div className={classes.folderList}>
                            {availableFolders.map((folder) => (
                              <Checkbox
                                key={folder.id}
                                isChecked={selectedFolderIds.includes(folder.id)}
                                onClick={() => {
                                  if (selectedFolderIds.includes(folder.id)) {
                                    setSelectedFolderIds((prev) =>
                                      prev.filter((id) => id !== folder.id)
                                    );
                                  } else {
                                    setSelectedFolderIds((prev) => [...prev, folder.id]);
                                  }
                                }}
                                label={folder.name}
                              />
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    {newUserErrors.folders &&
                      !(!isLoadingFolders && availableFolders.length === 0) && (
                      <div className={classes.error}>{newUserErrors.folders}</div>
                    )}
                  </div>
                </div>
              )}

              <div className={classes.formActions}>
                <div className={classes.formActionsRight} style={{ marginLeft: 'auto' }}>
                <button
                    className={classes.cancelFormButton}
                  onClick={handleCancelCreateUser}
                  type="button"
                >
                    Cancel
                </button>
                  <FilledButton
                    onClick={handleCreateUserSubmit}
                    width={240}
                    disabled={isAdding}
                  >
                    {isAdding ? 'Sending...' : 'Send invitation'}
                  </FilledButton>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
